import React, { useMemo, useState, useEffect } from 'react';
import { Checkbox, Divider, Collapse } from 'antd';

const { Panel } = Collapse;

const LocationCheckboxes = ({
  masterLocations,
  userLocations,
  updateSelectedLocations,
}) => {
  const initialChecked = useMemo(
    () => userLocations.map(loc => loc.id.toString()),
    [userLocations]
  );
  const [checkedList, setCheckedList] = useState(initialChecked);

  useEffect(() => {
    notifyParent(initialChecked);
  }, []);

  const notifyParent = (checkedIds) => {
    const selected = masterLocations.filter(loc =>
      checkedIds.includes(loc.id.toString())
    );
    updateSelectedLocations(selected);
  };

  const groupedByCityId = useMemo(() => {
    const byCity = {};
    masterLocations.forEach(loc => {
      const cityId = loc.city_id;
      if (!byCity[cityId]) {
        byCity[cityId] = {
          city_name: loc.city_name || 'Unknown City',
          locations: [],
        };
      }
      byCity[cityId].locations.push(loc);
    });
    return byCity;
  }, [masterLocations]);

  const getGroupedLocationsByCityId = () => {
    const grouped = {};
    for (const cityId in groupedByCityId) {
      const { locations, city_name } = groupedByCityId[cityId];
      const parents = locations.filter(loc => loc.parent_id === null);
      const childrenMap = {};

      locations.forEach(loc => {
        if (loc.parent_id) {
          if (!childrenMap[loc.parent_id]) childrenMap[loc.parent_id] = [];
          childrenMap[loc.parent_id].push(loc);
        }
      });

      grouped[cityId] = {
        city_name,
        groups: parents.map(parent => ({
          parent,
          children: childrenMap[parent.id] || [],
        })),
      };
    }
    return grouped;
  };

  const handleParentChange = (parentId, children) => {
    const parentIdStr = parentId.toString();
    const childIds = children.map(c => c.id.toString());

    const isChecked = checkedList.includes(parentIdStr);
    let newChecked;

    if (isChecked) {
      newChecked = checkedList.filter(id => id !== parentIdStr && !childIds.includes(id));
    } else {
      newChecked = [...new Set([...checkedList, parentIdStr, ...childIds])];
    }

    setCheckedList(newChecked);
    notifyParent(newChecked);
  };

  const handleChildrenChange = (parentId, selectedChildIds) => {
    const parentIdStr = parentId.toString();
    const allChildren = masterLocations.filter(loc => loc.parent_id === parentId);
    const allChildIds = allChildren.map(c => c.id.toString());

    let newChecked = checkedList.filter(id => !allChildIds.includes(id));
    newChecked = [...newChecked, ...selectedChildIds];

    if (allChildIds.every(id => selectedChildIds.includes(id))) {
      newChecked.push(parentIdStr);
    } else {
      newChecked = newChecked.filter(id => id !== parentIdStr);
    }

    newChecked = [...new Set(newChecked)];
    setCheckedList(newChecked);
    notifyParent(newChecked);
  };

  const groupedByCityWithHierarchy = useMemo(getGroupedLocationsByCityId, [groupedByCityId]);

  return (
    <Collapse accordion>
      {Object.entries(groupedByCityWithHierarchy).map(([cityId, { city_name, groups }]) => (
        <Panel header={city_name} key={cityId}>
          {groups.map(({ parent, children }) => {
            const parentIdStr = parent.id.toString();
            const childIds = children.map(c => c.id.toString());
            const selectedChildren = checkedList.filter(id => childIds.includes(id));

            return (
              <div key={parent.id} style={{ marginBottom: 24 }}>
                <Checkbox
                  checked={checkedList.includes(parentIdStr)}
                  indeterminate={
                    selectedChildren.length > 0 &&
                    selectedChildren.length < childIds.length
                  }
                  onChange={() => handleParentChange(parent.id, children)}
                >
                  <strong>{parent.name}</strong>
                </Checkbox>

                <Divider style={{ margin: '8px 0' }} />

                <Checkbox.Group
                  value={selectedChildren}
                  onChange={(list) => handleChildrenChange(parent.id, list)}
                  style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  {children.map(child => (
                    <Checkbox key={child.id} value={child.id.toString()}>
                      {child.name}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </div>
            );
          })}
        </Panel>
      ))}
    </Collapse>
  );
};

export default LocationCheckboxes;
